import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-assign-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogModule,
    MatIconModule
  ],
  templateUrl: './assign-dialog.component.html',
  styleUrls: ['./assign-dialog.component.scss']
})
export class AssignDialogComponent {
  trucks: any[];
  selectedTruckIds: number[] = [];
  maxSelectable: number;
  selectedTypeFilter: string | null = null;
  sortedTruckTypes: string[] = [];
  groupedTrucks: { type: string; trucks: any[] }[] = [];
  minRequiredWeight: number = 0;

  private _searchText: string = '';
  get searchText(): string { return this._searchText; }
  set searchText(value: string) {
    this._searchText = value;
    this.recompute();
  }

  constructor(
    public dialogRef: MatDialogRef<AssignDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.trucks = data.trucks;
    this.maxSelectable = data.order?.remainingTrucksNeeded ?? Infinity;
    this.minRequiredWeight = this.getMinRequiredWeight(data.order?.remainingByType);
    this.sortedTruckTypes = this.buildSortedTypes();
    this.recompute();
  }

  private parseTruckWeight(name: string): number {
    const match = name.match(/\d+(\.\d+)?/);
    return match ? parseFloat(match[0]) : Infinity;
  }

  private getMinRequiredWeight(remainingByType: { truckType: string; remaining: number }[] | undefined): number {
    if (!remainingByType || remainingByType.length === 0) return 0;
    const weights = remainingByType
      .filter(r => r.remaining > 0)
      .map(r => this.parseTruckWeight(r.truckType));
    return weights.length > 0 ? Math.min(...weights) : 0;
  }

  isUndersized(truck: any): boolean {
    if (!this.minRequiredWeight) return false;
    return this.parseTruckWeight(truck.truckTypeName) < this.minRequiredWeight;
  }

  private buildSortedTypes(): string[] {
    const types = new Set<string>(
      this.trucks.map(t => t.truckTypeName).filter(n => !!n)
    );
    return Array.from(types).sort(
      (a, b) => this.parseTruckWeight(a) - this.parseTruckWeight(b)
    );
  }

  private recompute(): void {
    const filtered = this.trucks
      .filter(truck => {
        const matchesType = this.selectedTypeFilter === null || truck.truckTypeName === this.selectedTypeFilter;
        const matchesSearch = !this._searchText || truck.name.toLowerCase().includes(this._searchText.toLowerCase());
        return matchesType && matchesSearch;
      })
      .sort((a, b) => this.parseTruckWeight(a.truckTypeName) - this.parseTruckWeight(b.truckTypeName));

    const map = new Map<string, any[]>();
    for (const truck of filtered) {
      const key = truck.truckTypeName || 'Unknown';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(truck);
    }
    this.groupedTrucks = Array.from(map.entries()).map(([type, trucks]) => ({ type, trucks }));
  }

  setTypeFilter(type: string | null): void {
    this.selectedTypeFilter = type;
    this.recompute();
  }

  get totalFilteredCount(): number {
    return this.groupedTrucks.reduce((sum, g) => sum + g.trucks.length, 0);
  }

  isSelected(truckId: number): boolean {
    return this.selectedTruckIds.includes(truckId);
  }

  get limitReached(): boolean {
    return this.selectedTruckIds.length >= this.maxSelectable;
  }

  isDisabled(truckId: number): boolean {
    if (this.selectedTruckIds.includes(truckId)) return false;
    const truck = this.trucks.find(t => t.id === truckId);
    if (truck && this.isUndersized(truck)) return true;
    return this.limitReached;
  }

  toggleTruck(truckId: number): void {
    if (this.isDisabled(truckId)) return;
    const idx = this.selectedTruckIds.indexOf(truckId);
    if (idx === -1) {
      this.selectedTruckIds = [...this.selectedTruckIds, truckId];
    } else {
      this.selectedTruckIds = this.selectedTruckIds.filter(id => id !== truckId);
    }
  }

  onConfirm(): void {
    this.dialogRef.close(this.selectedTruckIds);
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
